import { c as _c } from "react/compiler-runtime";
import { Box, Button, Flex, HStack, Icon, Menu, Portal, Progress, Tabs, Text, VStack } from '@chakra-ui/react';
import { LuCheck, LuChevronDown } from 'react-icons/lu';
export type ApplicationStepKey = 'about' | 'expertise' | 'background' | 'advising';
export const APPLICATION_STEPS: Array<{
  key: ApplicationStepKey;
  title: string;
  blurb: string;
}> = [{
  key: 'about',
  title: 'About You',
  blurb: 'Name, location, photo'
}, {
  key: 'expertise',
  title: 'Expertise',
  blurb: 'What you advise on'
}, {
  key: 'background',
  title: 'Background',
  blurb: 'How you introduce yourself'
}, {
  key: 'advising',
  title: 'How you advise',
  blurb: 'Terms and availability'
}];
type StepperSharedProps = {
  mode: 'wizard' | 'sections';
  activeKey: ApplicationStepKey;
  furthestIndex: number;
  complete: Record<ApplicationStepKey, boolean>;
  onSelect: (key: ApplicationStepKey) => void;
};
const isStepReachable = (mode: StepperSharedProps['mode'], index: number, furthestIndex: number) => mode === 'sections' || index <= furthestIndex;
const StepMark = t0 => {
  const $ = _c(7);
  const {
    done,
    isActive,
    index
  } = t0;
  const t1 = done ? "green.muted" : isActive ? "colorPalette.solid/20" : "colorPalette.solid/10";
  const t2 = done ? "green.fg" : "colorPalette.fg/70";
  let t3;
  if ($[0] !== done || $[1] !== index) {
    t3 = done ? <Icon boxSize={3.5}><LuCheck /></Icon> : index + 1;
    $[0] = done;
    $[1] = index;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = <Flex align="center" bg={t1} borderRadius="full" boxSize={6} color={t2} flexShrink={0} fontSize="xs" fontWeight="bold" justify="center">{t3}</Flex>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const StepLabel = t0 => {
  const $ = _c(8);
  const {
    title,
    blurb,
    hideBlurbBelow
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text fontSize="sm" fontWeight="medium" lineClamp={1}>{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== blurb || $[3] !== hideBlurbBelow) {
    t2 = <Text color="colorPalette.fg/55" fontSize="xs" hideBelow={hideBlurbBelow} lineClamp={1} lineHeight="normal">{blurb}</Text>;
    $[2] = blurb;
    $[3] = hideBlurbBelow;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Box minWidth={0} textAlign="start">{t1}{t2}</Box>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};

/** Compact step picker for narrow / mobile layouts. */
export const ApplicationStepMenu = t0 => {
  const $ = _c(28);
  const {
    mode,
    activeKey,
    furthestIndex,
    complete,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== activeKey) {
    t1 = APPLICATION_STEPS.find(step => step.key === activeKey) || APPLICATION_STEPS[0];
    $[0] = activeKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const active = t1;
  let t2;
  if ($[2] !== active.key) {
    t2 = APPLICATION_STEPS.findIndex(step_0 => step_0.key === active.key);
    $[2] = active.key;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const activeIndex = t2;
  const activeDone = complete[active.key];
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      placement: "bottom",
      sameWidth: true
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `${active.title}. Choose application step`;
  let t5;
  if ($[5] !== activeDone || $[6] !== activeIndex) {
    t5 = <StepMark done={activeDone} index={activeIndex} isActive={true} />;
    $[5] = activeDone;
    $[6] = activeIndex;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== active.title) {
    t6 = <Text fontSize="sm" fontWeight="medium" lineClamp={1}>{active.title}</Text>;
    $[8] = active.title;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <HStack gap={2} minWidth={0}>{t5}{t6}</HStack>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon boxSize={4} color="colorPalette.fg/60" flexShrink={0}><LuChevronDown /></Icon>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t4 || $[15] !== t7) {
    t9 = <Menu.Trigger asChild={true}><Button aria-label={t4} height="auto" justifyContent="space-between" paddingY={2} variant="outline" width="full">{t7}{t8}</Button></Menu.Trigger>;
    $[14] = t4;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== activeKey || $[18] !== complete || $[19] !== furthestIndex || $[20] !== mode || $[21] !== onSelect) {
    t10 = APPLICATION_STEPS.map((step_1, index) => {
      const reachable = isStepReachable(mode, index, furthestIndex);
      const done = complete[step_1.key];
      const isActive = step_1.key === activeKey;
      return <Menu.Item key={step_1.key} disabled={!reachable} value={step_1.key} onSelect={() => {
        if (reachable) {
          onSelect(step_1.key);
        }
      }}><HStack align="center" gap={2} minWidth={0} width="full"><StepMark done={done} index={index} isActive={isActive} /><StepLabel blurb={step_1.blurb} title={step_1.title} /></HStack></Menu.Item>;
    });
    $[17] = activeKey;
    $[18] = complete;
    $[19] = furthestIndex;
    $[20] = mode;
    $[21] = onSelect;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <Portal><Menu.Positioner><Menu.Content>{t10}</Menu.Content></Menu.Positioner></Portal>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <Menu.Root positioning={t3}>{t9}{t11}</Menu.Root>;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
};
const ApplicationStepTabs = t0 => {
  const $ = _c(15);
  const {
    mode,
    activeKey,
    furthestIndex,
    complete,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== furthestIndex || $[1] !== mode || $[2] !== onSelect) {
    t1 = details => {
      const key = details.value as ApplicationStepKey;
      const index = APPLICATION_STEPS.findIndex(step => step.key === key);
      if (index >= 0 && isStepReachable(mode, index, furthestIndex)) {
        onSelect(key);
      }
    };
    $[0] = furthestIndex;
    $[1] = mode;
    $[2] = onSelect;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== activeKey || $[5] !== complete || $[6] !== furthestIndex || $[7] !== mode) {
    t2 = APPLICATION_STEPS.map((step_0, index_0) => {
      const isActive = step_0.key === activeKey;
      const reachable = isStepReachable(mode, index_0, furthestIndex);
      const done = complete[step_0.key];
      return <Tabs.Trigger key={step_0.key} alignItems="center" disabled={!reachable} height="auto" justifyContent="flex-start" minH="var(--tabs-height)" paddingY={2} textAlign="start" value={step_0.key}><HStack align="center" gap={2} minWidth={0} width="full"><StepMark done={done} index={index_0} isActive={isActive} /><StepLabel blurb={step_0.blurb} hideBlurbBelow="lg" title={step_0.title} /></HStack></Tabs.Trigger>;
    });
    $[4] = activeKey;
    $[5] = complete;
    $[6] = furthestIndex;
    $[7] = mode;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <Tabs.List width="full">{t2}</Tabs.List>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== activeKey || $[12] !== t1 || $[13] !== t3) {
    t4 = <Tabs.Root fitted={true} size="md" value={activeKey} variant="enclosed" width="full" onValueChange={t1}>{t3}</Tabs.Root>;
    $[11] = activeKey;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};

/**
 * Step navigation.
 *
 * `mode: 'wizard'` (first application) walks forward and only unlocks steps the
 * applicant has reached. `mode: 'sections'` (editing a live profile) lets every
 * step be opened directly — changing a headline should not mean walking the
 * wizard. Review is a post-step interstitial on apply, not a tab.
 *
 * Below `md`, steps collapse to a single menu so they don't eat the form.
 */
export const ApplicationStepper = t0 => {
  const $ = _c(26);
  const {
    mode,
    activeKey,
    furthestIndex,
    complete,
    percentComplete,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== activeKey || $[1] !== complete || $[2] !== furthestIndex || $[3] !== mode || $[4] !== onSelect) {
    t1 = {
      activeKey,
      complete,
      furthestIndex,
      mode,
      onSelect
    };
    $[0] = activeKey;
    $[1] = complete;
    $[2] = furthestIndex;
    $[3] = mode;
    $[4] = onSelect;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const shared = t1;
  const t2 = mode === "wizard" ? "Advisor application" : "Advisor profile";
  let t3;
  if ($[6] !== t2) {
    t3 = <Text color="colorPalette.fg/60" textStyle="labelXs">{t2}</Text>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== percentComplete) {
    t4 = <Text color="colorPalette.fg/60" fontSize="xs">{percentComplete}% complete</Text>;
    $[8] = percentComplete;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <HStack gap={3} justify="space-between" wrap="wrap">{t3}{t4}</HStack>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = percentComplete === 100 ? "green" : "orange";
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Progress.Track><Progress.Range /></Progress.Track>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== percentComplete || $[15] !== t6) {
    t8 = <Progress.Root colorPalette={t6} size="xs" value={percentComplete}>{t7}</Progress.Root>;
    $[14] = percentComplete;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== shared) {
    t9 = <Box hideFrom="md"><ApplicationStepMenu {...shared} /></Box>;
    $[17] = shared;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== shared) {
    t10 = <Box hideBelow="md"><ApplicationStepTabs {...shared} /></Box>;
    $[19] = shared;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t5 || $[23] !== t8 || $[24] !== t9) {
    t11 = <VStack align="stretch" gap={3}>{t5}{t8}{t9}{t10}</VStack>;
    $[21] = t10;
    $[22] = t5;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};